"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import Link from "next/link";
import { useChapterAudio } from "@/hooks/use-chapter-audio";
import { AudioPlayerProgress } from "./audio-player-progress";
import { AudioPlayerControls } from "./audio-player-controls";
import { AlertCircle, Play, X } from "lucide-react";
interface AudioPlayerProps {
  storySlug: string;
  chapterId: string;
  nextChapterId?: string;
  isAuthenticated?: boolean;
  autoPlay?: boolean;
  onChapterEnd?: () => void;
}
export function ChapterAudioPlayer(props) {
  const $ = _c(33);
  const audioState = useChapterAudio(props);
  const [isExpanded, setIsExpanded] = useState(props.autoPlay || false);
  const [previousAutoPlay, setPreviousAutoPlay] = useState(props.autoPlay);
  if (previousAutoPlay !== props.autoPlay) {
    setPreviousAutoPlay(props.autoPlay);
    if (props.autoPlay) {
      setIsExpanded(true);
    }
  }
  if (!isExpanded) {
    let t0;
    if ($[0] !== audioState) {
      t0 = () => {
        setIsExpanded(true);
        audioState.togglePlay();
      };
      $[0] = audioState;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    let t1;
    if ($[2] !== audioState.isLoading) {
      t1 = audioState.isLoading ? <AlertCircle className="size-6 animate-pulse ml-1" /> : <Play className="size-6 fill-current ml-1" />;
      $[2] = audioState.isLoading;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== t0 || $[5] !== t1) {
      t2 = <button onClick={t0} className="absolute right-4 md:right-8 bottom-full mb-4 z-50 flex items-center justify-center size-14 bg-primary text-primary-foreground shadow-xl shadow-primary/20 rounded-full hover:scale-105 active:scale-95 transition-all" aria-label="Open Audio Player">{t1}</button>;
      $[4] = t0;
      $[5] = t1;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  let t0;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => setIsExpanded(false);
    $[7] = t0;
  } else {
    t0 = $[7];
  }
  let t1;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <button onClick={t0} className="absolute -top-12 right-4 sm:right-8 p-2 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100 transition-all z-50 rounded-full bg-white dark:bg-slate-800 shadow-md border border-slate-200 dark:border-slate-700 hover:scale-105 active:scale-95 flex items-center justify-center" aria-label="Close Player"><X className="size-5" strokeWidth={2.5} /></button>;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== audioState.error || $[10] !== audioState.errorCode || $[11] !== audioState.isLoading) {
    t2 = audioState.error && !audioState.isLoading && <div className="absolute bottom-full left-0 right-0 mb-3 text-sm text-red-200 font-medium flex items-center justify-between animate-in fade-in slide-in-from-bottom-2 bg-red-950/80 backdrop-blur-md px-4 py-2.5 rounded-xl border border-red-500/40"><div className="flex items-center gap-2"><AlertCircle className="size-4 shrink-0" /><p className="truncate">{audioState.error}</p></div>{audioState.errorCode === "upgrade_required" && <Link href="/reader-pricing" className="ml-4 shrink-0 hover:underline">Upgrade</Link>}{audioState.errorCode === "login_required" && <Link href="/signup" className="ml-4 shrink-0 hover:underline">Sign Up</Link>}</div>;
    $[9] = audioState.error;
    $[10] = audioState.errorCode;
    $[11] = audioState.isLoading;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  let t4;
  if ($[13] !== audioState) {
    t3 = () => audioState.setIsDragging(true);
    t4 = () => audioState.setIsDragging(false);
    $[13] = audioState;
    $[14] = t3;
    $[15] = t4;
  } else {
    t3 = $[14];
    t4 = $[15];
  }
  let t5;
  if ($[16] !== audioState.audioUrl || $[17] !== audioState.currentTime || $[18] !== audioState.duration || $[19] !== audioState.handleSeek || $[20] !== t3 || $[21] !== t4) {
    t5 = <AudioPlayerProgress currentTime={audioState.currentTime} duration={audioState.duration} audioUrl={audioState.audioUrl} onSeek={audioState.handleSeek} onDragStart={t3} onDragEnd={t4} />;
    $[16] = audioState.audioUrl;
    $[17] = audioState.currentTime;
    $[18] = audioState.duration;
    $[19] = audioState.handleSeek;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] !== audioState.audioUrl || $[24] !== audioState.isLoading || $[25] !== audioState.isPlaying || $[26] !== audioState.skip || $[27] !== audioState.togglePlay) {
    t6 = <AudioPlayerControls isPlaying={audioState.isPlaying} isLoading={audioState.isLoading} audioUrl={audioState.audioUrl} onTogglePlay={audioState.togglePlay} onSkip={audioState.skip} />;
    $[23] = audioState.audioUrl;
    $[24] = audioState.isLoading;
    $[25] = audioState.isPlaying;
    $[26] = audioState.skip;
    $[27] = audioState.togglePlay;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== t2 || $[30] !== t5 || $[31] !== t6) {
    t7 = <div className="absolute bottom-full left-0 right-0 z-40 bg-white/95 dark:bg-[#1f1d35]/95 backdrop-blur-md border-y border-slate-200/50 dark:border-slate-800/80 shadow-[0_-4px_20px_rgba(0,0,0,0.05)] pt-3 pb-3 px-4 sm:px-6">{t1}<div className="max-w-3xl mx-auto flex flex-col gap-2 relative">{t2}{t5}{t6}</div></div>;
    $[29] = t2;
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  return t7;
}